/**
 * Landing Page
 *
 * Public landing page showing:
 * 1. Assessed principals statistics
 * 2. Assessment statistics by role (charts)
 * 3. SMK school list with website links
 * 4. Flood status with photos
 * 5. KJP recipient status
 * 6. KIP recipient status
 * 7. Prestasi Minggu Ini (Weekly Achievement List)
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { prestasiService } from '../services/supabase';

const LandingPage = () => {
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(true);
  const [assessedPrincipals, setAssessedPrincipals] = useState([]);
  const [assessmentStats, setAssessmentStats] = useState([]);
  const [schools, setSchools] = useState([]);
  const [assessedSchoolsCount, setAssessedSchoolsCount] = useState(0);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [prestasiMingguIni, setPrestasiMingguIni] = useState([]);
  const [selectedPrestasi, setSelectedPrestasi] = useState(null);
  const [showPrestasiModal, setShowPrestasiModal] = useState(false);

  // Default school (SMKN 1 Jakarta - first in list or a specific one)
  const DEFAULT_SCHOOL_NPSN = '20102592'; // SMK NEGERI 1 JAKARTA

  useEffect(() => {
    loadLandingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLandingData = async () => {
    try {
      setLoading(true);

      // Load all data in parallel
      const [principalsResult, statsResult, schoolsResult, assessedCount, prestasiResult] = await Promise.all([
        loadAssessedPrincipals(),
        loadAssessmentStats(),
        loadSchools(),
        loadAssessedSchoolsCount(),
        loadPrestasiMingguIni()
      ]);

      setAssessedPrincipals(principalsResult);
      setAssessmentStats(statsResult);
      setSchools(schoolsResult);
      setPrestasiMingguIni(prestasiResult);
      setAssessedSchoolsCount(assessedCount);

    } catch (error) {
      console.error('Error loading landing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAssessedPrincipals = async () => {
    // Get completed assessment sessions from evaluators (teachers, parents, supervisors)
    // These sessions represent assessments OF principals
    const { data: sessions, error: sessionsError } = await supabase
      .from('assessment_sessions')
      .select(`
        id,
        user_id,
        status,
        end_time,
        auto_save_data,
        target_school_id
      `)
      .eq('status', 'completed')
      .order('end_time', { ascending: false })
      .limit(20);

    if (sessionsError) {
      console.error('Error loading sessions:', sessionsError);
      return [];
    }

    if (!sessions || sessions.length === 0) {
      return [];
    }

    // Get evaluator details (teacher, parent, supervisor who assessed principals)
    const { data: users, error: usersError } = await supabase
      .from('users_pkks')
      .select('id, full_name, school_id, role');

    if (usersError) {
      console.error('Error loading users:', usersError);
      return [];
    }

    // Get schools for display
    const schoolIds = [...new Set(sessions.map(s => s.target_school_id).filter(Boolean))];
    const { data: schoolsData } = schoolIds.length > 0 ? await supabase
      .from('sekolah')
      .select('id, nama')
      .in('id', schoolIds) : { data: [] };

    const schoolMap = (schoolsData || []).reduce((acc, s) => {
      acc[s.id] = s;
      return acc;
    }, {});

    // Create a map of users for quick lookup
    const userMap = (users || []).reduce((acc, user) => {
      acc[user.id] = user;
      return acc;
    }, {});

    // Map sessions to assessment records showing who assessed which school's principal
    const assessedList = sessions.map(session => {
      const user = userMap[session.user_id];
      const role = user?.role || session.auto_save_data?.role || 'unknown';
      const targetSchool = session.target_school_id ? schoolMap[session.target_school_id] : null;
      return {
        id: session.id,
        evaluatorName: user?.full_name || `Anonymous ${role}`,
        evaluatorRole: role,
        school: targetSchool?.nama || user?.school_id || 'Unknown School',
        completedDate: session.end_time
      };
    }).filter(item => {
      // Include all evaluator roles (teachers, parents, supervisors, students, industry partners)
      // Only exclude principals who might be doing self-assessments
      const evaluatorRoles = ['teacher', 'parent', 'supervisor', 'student', 'industry_partner'];
      return evaluatorRoles.includes(item.evaluatorRole);
    });

    return assessedList;
  };

  const loadAssessedSchoolsCount = async () => {
    // Count unique schools that have at least one completed assessment
    const { data: sessions, error } = await supabase
      .from('assessment_sessions')
      .select('target_school_id')
      .eq('status', 'completed')
      .not('target_school_id', 'is', null);

    if (error) {
      console.error('Error loading assessed schools:', error);
      return 0;
    }

    // Count unique schools
    const uniqueSchools = new Set(sessions?.map(s => s.target_school_id) || []);
    return uniqueSchools.size;
  };

  const loadAssessmentStats = async () => {
    const { data, error } = await supabase
      .from('view_completion_rates')
      .select('*');

    if (error) {
      console.error('Error loading stats:', error);
      return [];
    }

    return data || [];
  };

  const loadSchools = async () => {
    const { data, error } = await supabase
      .from('sekolah')
      .select('*')
      .eq('status', 'aktif')
      .order('nama')
      .limit(50);

    if (error) {
      console.error('Error loading schools:', error);
      return [];
    }

    return data || [];
  };

  // Helper: returns { weekStart, weekEnd } as UTC Monday–Sunday date strings (YYYY-MM-DD)
  // Matches PostgreSQL date_trunc('week', ...) which uses ISO 8601 Monday-start weeks.
  const getWeekBounds = () => {
    const now = new Date();
    const day = now.getUTCDay(); // 0=Sun … 6=Sat
    const weekStart = new Date(Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() - ((day + 6) % 7) // roll back to Monday
    ));
    const weekEnd = new Date(Date.UTC(
      weekStart.getUTCFullYear(),
      weekStart.getUTCMonth(),
      weekStart.getUTCDate() + 6 // Sunday
    ));
    return {
      weekStartStr: weekStart.toISOString().slice(0, 10),
      weekEndStr: weekEnd.toISOString().slice(0, 10),
      weekStartISO: weekStart.toISOString()
    };
  };

  // Helper: extract date string (YYYY-MM-DD) from a timestamp string
  const extractDate = (timestamp) => (timestamp ? timestamp.split('T')[0] : null);

  // Helper: map a sekolah row to the prestasi display shape
  const mapSekolahToPrestasi = (s, judul, deskripsi) => ({
    id: s.id,
    nama_sekolah: s.nama,
    judul,
    deskripsi,
    photo_urls: [s.foto_gedung, s.foto_ruang_kelas, s.foto_lapangan].filter(Boolean),
    tanggal_prestasi: extractDate(s.updated_at),
    kecamatan: s.kecamatan
  });

  const loadPrestasiMingguIni = async () => {
    const { weekStartStr, weekEndStr, weekStartISO } = getWeekBounds();

    // Primary: load from prestasi_mingguan table.
    // photo_urls is a TEXT[] of public URLs from the 'prestasi-foto' S3 bucket.
    const { data: prestasiData, error: prestasiError } = await prestasiService.getWeeklyPrestasi(
      weekStartStr,
      weekEndStr
    );

    if (!prestasiError && prestasiData && prestasiData.length > 0) {
      return prestasiData;
    }

    // Fallback: show schools that have completed documentation this week.
    // foto_gedung/ruang_kelas/lapangan also store public bucket URLs.
    const { data: sekolahData, error: sekolahError } = await supabase
      .from('sekolah')
      .select('id, nama, foto_gedung, foto_ruang_kelas, foto_lapangan, status_dokumentasi, updated_at, kecamatan')
      .eq('status', 'aktif')
      .eq('status_dokumentasi', 'selesai_upload')
      .gte('updated_at', weekStartISO)
      .order('updated_at', { ascending: false })
      .limit(6);

    if (!sekolahError && sekolahData && sekolahData.length > 0) {
      return sekolahData.map(s => mapSekolahToPrestasi(
        s,
        'Dokumentasi Selesai',
        `${s.nama} telah menyelesaikan dokumentasi minggu ini`
      ));
    }

    // Final fallback: show any schools with photos
    const { data: anySekolah, error: anyError } = await supabase
      .from('sekolah')
      .select('id, nama, foto_gedung, foto_ruang_kelas, foto_lapangan, status_dokumentasi, updated_at, kecamatan')
      .eq('status', 'aktif')
      .not('foto_gedung', 'is', null)
      .order('updated_at', { ascending: false })
      .limit(6);

    if (anyError) {
      console.error('Error loading prestasi:', anyError);
      return [];
    }

    return (anySekolah || []).map(s => mapSekolahToPrestasi(
      s,
      'Dokumentasi Sekolah',
      `Dokumentasi ${s.nama}`
    ));
  };

  const getRoleLabel = (role) => {
    const labels = {
      teacher: 'Guru',
      student: 'Siswa',
      industry_partner: 'Industri',
      principal: 'Kepala Sekolah',
      parent: 'Orang Tua',
      supervisor: 'Pengawas'
    };
    return labels[role] || role;
  };

  const getRoleColor = (role) => {
    const colors = {
      teacher: 'bg-blue-500',
      student: 'bg-green-500',
      industry_partner: 'bg-orange-500',
      principal: 'bg-purple-500',
      parent: 'bg-cyan-500',
      supervisor: 'bg-red-500'
    };
    return colors[role] || 'bg-gray-500';
  };

  const getRoleGradient = (role) => {
    const gradients = {
      teacher: 'bg-gradient-to-br from-blue-500 to-blue-600',
      student: 'bg-gradient-to-br from-green-500 to-green-600',
      industry_partner: 'bg-gradient-to-br from-orange-500 to-orange-600',
      principal: 'bg-gradient-to-br from-purple-500 to-purple-600',
      parent: 'bg-gradient-to-br from-cyan-500 to-cyan-600',
      supervisor: 'bg-gradient-to-br from-red-500 to-red-600'
    };
    return gradients[role] || 'bg-gradient-to-br from-gray-500 to-gray-600';
  };

  const getRoleIcon = (role) => {
    const icons = {
      teacher: (
        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      student: (
        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      industry_partner: (
        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      principal: (
        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      parent: (
        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      supervisor: (
        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    };
    return icons[role] || (
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    );
  };

  const getCompletionBadgeColor = (rate) => {
    if (rate >= 80) return 'bg-green-100 text-green-700';
    if (rate >= 50) return 'bg-yellow-100 text-yellow-700';
    if (rate > 0) return 'bg-orange-100 text-orange-700';
    return 'bg-gray-100 text-gray-600';
  };

  const getCompletionStatus = (rate) => {
    if (rate >= 80) return 'Sangat Baik';
    if (rate >= 50) return 'Cukup Baik';
    if (rate > 0) return 'Perlu Ditingkatkan';
    return 'Belum Dimulai';
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      teacher: 'bg-blue-100 text-blue-700',
      student: 'bg-green-100 text-green-700',
      industry_partner: 'bg-orange-100 text-orange-700',
      principal: 'bg-purple-100 text-purple-700',
      parent: 'bg-cyan-100 text-cyan-700',
      supervisor: 'bg-red-100 text-red-700'
    };
    return colors[role] || 'bg-gray-100 text-gray-700';
  };

  const getTotalAssessments = () => {
    return assessmentStats.reduce((sum, stat) => sum + (stat.total_sessions || 0), 0);
  };

  const getCompletedAssessments = () => {
    return assessmentStats.reduce((sum, stat) => sum + (stat.completed_sessions || 0), 0);
  };

  const getTotalKJPRecipients = () => {
    return schools.reduce((sum, school) => sum + (school.jumlah_penerima_kjp || 0), 0);
  };

  const getTotalPIPRecipients = () => {
    return schools.reduce((sum, school) => sum + (school.jumlah_penerima_pip || 0), 0);
  };

  const getAssessmentCoverage = () => {
    if (schools.length === 0) return 0;
    return Math.round((assessedSchoolsCount / schools.length) * 100);
  };

  // Get default school
  const defaultSchool = schools.find(s => s.npsn === DEFAULT_SCHOOL_NPSN) || schools[0];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat data...</p>
        </div>
      </div>
    );
  }

  const assessmentCoverage = getAssessmentCoverage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">P</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">PKKS</h1>
                <p className="text-xs text-gray-500">Penilaian Kinerja Kepala Sekolah</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Login
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Sistem Penilaian Kinerja Kepala Sekolah
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Platform penilaian kinerja kepala sekolah SMK berbasis rubrik PKKS dengan partisipasi multi-stakeholder
          </p>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 max-w-6xl mx-auto">
            <div className="bg-white rounded-xl p-5 shadow-md hover:shadow-lg transition-shadow">
              <div className="text-3xl font-bold text-blue-600">{getTotalAssessments()}</div>
              <div className="text-sm text-gray-500">Total Penilaian</div>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-md hover:shadow-lg transition-shadow">
              <div className="text-3xl font-bold text-green-600">{getCompletedAssessments()}</div>
              <div className="text-sm text-gray-500">Selesai</div>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-md hover:shadow-lg transition-shadow">
              <div className="text-3xl font-bold text-orange-600">{schools.length}</div>
              <div className="text-sm text-gray-500">Total Sekolah</div>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-md hover:shadow-lg transition-shadow">
              <div className="text-3xl font-bold text-purple-600">{assessedSchoolsCount}</div>
              <div className="text-sm text-gray-500">Kepsek Dinilai</div>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-md hover:shadow-lg transition-shadow">
              <div className="text-3xl font-bold text-emerald-600">{getTotalKJPRecipients()}</div>
              <div className="text-sm text-gray-500">Penerima KJP</div>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-md hover:shadow-lg transition-shadow">
              <div className="text-3xl font-bold text-teal-600">{getTotalPIPRecipients()}</div>
              <div className="text-sm text-gray-500">Penerima PIP</div>
            </div>
          </div>

          {/* Assessment Coverage Graphic */}
          <div className="mt-8 max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                {/* Circular Progress */}
                <div className="relative">
                  <svg className="w-40 h-40 transform -rotate-90">
                    {/* Background circle */}
                    <circle
                      cx="80"
                      cy="80"
                      r="70"
                      stroke="#e5e7eb"
                      strokeWidth="12"
                      fill="none"
                    />
                    {/* Progress circle */}
                    <circle
                      cx="80"
                      cy="80"
                      r="70"
                      stroke="url(#gradient)"
                      strokeWidth="12"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${(assessmentCoverage / 100) * 440} 440`}
                      className="transition-all duration-1000 ease-out"
                    />
                    <defs>
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#3b82f6" />
                        <stop offset="100%" stopColor="#8b5cf6" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-4xl font-bold text-gray-900">{assessmentCoverage}%</span>
                    <span className="text-sm text-gray-500">Cakupan</span>
                  </div>
                </div>

                {/* Stats Info */}
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Cakupan Penilaian Kepala Sekolah
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded-full bg-gradient-to-r from-blue-500 to-purple-500"></div>
                      <span className="text-gray-600">
                        <span className="font-bold text-gray-900">{assessedSchoolsCount}</span> dari{' '}
                        <span className="font-bold text-gray-900">{schools.length}</span> sekolah telah dinilai
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded-full bg-gray-200"></div>
                      <span className="text-gray-600">
                        <span className="font-bold text-gray-900">{schools.length - assessedSchoolsCount}</span> sekolah belum dinilai
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded-full bg-green-500"></div>
                      <span className="text-gray-600">
                        <span className="font-bold text-gray-900">{getCompletedAssessments()}</span> penilaian selesai
                      </span>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="mt-6">
                    <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-1000"
                        style={{ width: `${assessmentCoverage}%` }}
                      />
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      Target: 100% kepala sekolah dinilai
                    </p>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="flex flex-col gap-3">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-4 text-center min-w-[100px]">
                    <div className="text-2xl font-bold">{assessedSchoolsCount}</div>
                    <div className="text-xs opacity-80">Dinilai</div>
                  </div>
                  <div className="bg-gradient-to-br from-gray-400 to-gray-500 text-white rounded-xl p-4 text-center min-w-[100px]">
                    <div className="text-2xl font-bold">{schools.length - assessedSchoolsCount}</div>
                    <div className="text-xs opacity-80">Belum</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Access Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto mt-8">
            <div
              onClick={() => navigate('/login?role=parent')}
              className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-blue-500"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 text-center mb-2">Penilaian Orang Tua</h3>
              <p className="text-sm text-gray-500 text-center">Nilai kinerja kepala sekolah sebagai orang tua/wali</p>
            </div>

            <div
              onClick={() => navigate('/login?role=teacher')}
              className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-green-500"
            >
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 text-center mb-2">Penilaian Guru</h3>
              <p className="text-sm text-gray-500 text-center">Nilai kinerja kepala sekolah sebagai guru</p>
            </div>

            <div
              onClick={() => navigate('/login?role=student')}
              className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-purple-500"
            >
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 text-center mb-2">Survei Siswa</h3>
              <p className="text-sm text-gray-500 text-center">Ikut survei kepuasan siswa sebagai siswa</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 1: Recent Assessments */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <span className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </span>
            Penilaian Kepala Sekolah Terbaru
          </h3>

          {assessedPrincipals.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {assessedPrincipals.map((assessment) => (
                <div key={assessment.id} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${getRoleColor(assessment.evaluatorRole)}`}>
                      {assessment.evaluatorName?.charAt(0) || 'A'}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{assessment.evaluatorName}</h4>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${getRoleBadgeColor(assessment.evaluatorRole)}`}>
                          {getRoleLabel(assessment.evaluatorRole)}
                        </span>
                        <span className="text-sm text-gray-500">{assessment.school || 'SMK'}</span>
                      </div>
                      <p className="text-xs text-green-600 mt-1">
                        Menilai: {assessment.completedDate ? new Date(assessment.completedDate).toLocaleDateString('id-ID') : '-'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-xl">
              <p className="text-gray-500">Belum ada penilaian kepala sekolah yang selesai</p>
            </div>
          )}
        </div>
      </section>

      {/* Section 2: Assessment Statistics */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Progres Penilaian Kinerja Kepala Sekolah
            </h3>
            <p className="text-gray-600">
              Partisipasi dari berbagai pemangku kepentingan dalam penilaian kinerja kepala sekolah
            </p>
          </div>

          {assessmentStats.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {assessmentStats.map((stat) => (
                <div
                  key={stat.role}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  {/* Header with gradient */}
                  <div className={`${getRoleGradient(stat.role)} p-4`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                          {getRoleIcon(stat.role)}
                        </div>
                        <div>
                          <h4 className="font-bold text-white text-lg">{getRoleLabel(stat.role)}</h4>
                          <p className="text-white/80 text-sm">Penilai</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-white">
                          {Math.round(stat.completion_rate || 0)}%
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="p-5">
                    {/* Progress bar */}
                    <div className="mb-4">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-600">Tingkat Penyelesaian</span>
                        <span className="font-semibold text-gray-900">{stat.completed_sessions || 0}/{stat.total_sessions || 0}</span>
                      </div>
                      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${getRoleColor(stat.role)} rounded-full transition-all duration-700 ease-out`}
                          style={{ width: `${Math.min(stat.completion_rate || 0, 100)}%` }}
                        />
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-gray-50 rounded-xl p-3 text-center">
                        <div className="text-2xl font-bold text-gray-900">{stat.total_sessions || 0}</div>
                        <div className="text-xs text-gray-500">Total Sesi</div>
                      </div>
                      <div className="bg-green-50 rounded-xl p-3 text-center">
                        <div className="text-2xl font-bold text-green-600">{stat.completed_sessions || 0}</div>
                        <div className="text-xs text-green-600">Selesai</div>
                      </div>
                    </div>

                    {/* Status badge */}
                    <div className="mt-4 flex justify-center">
                      <span className={`px-4 py-1.5 rounded-full text-sm font-medium ${getCompletionBadgeColor(stat.completion_rate)}`}>
                        {getCompletionStatus(stat.completion_rate)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-md p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-gray-700 mb-2">Belum Ada Data Penilaian</h4>
              <p className="text-gray-500">Data penilaian akan muncul setelah ada penilai yang menyelesaikan asesmen</p>
            </div>
          )}
        </div>
      </section>

      {/* Section 3: School List */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <span className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </span>
            Daftar SMK di Jakarta Selatan 2
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {schools.slice(0, 12).map((school) => (
              <div
                key={school.id}
                className="bg-gray-50 rounded-xl p-4 border border-gray-100 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => {
                  setSelectedSchool(school);
                  setShowPhotoModal(true);
                }}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-semibold text-gray-900">{school.nama}</h4>
                    <p className="text-sm text-gray-500">NPSN: {school.npsn}</p>
                    <p className="text-sm text-gray-400">{school.kecamatan}</p>
                  </div>
                  {school.website && (
                    <a
                      href={school.website.startsWith('http') ? school.website : `https://${school.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  )}
                </div>

                {/* Status badges */}
                <div className="flex gap-2 mt-3 flex-wrap">
                  <span className={`px-2 py-1 text-xs rounded-full ${school.status_dokumentasi === 'selesai_upload' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    {school.status_dokumentasi === 'selesai_upload' ? 'Dokumentasi OK' : 'Belum Dokumentasi'}
                  </span>
                  <span className={`px-2 py-1 text-xs rounded-full ${school.jumlah_penerima_kjp > 0 ? 'bg-emerald-100 text-emerald-700' : school.status_kjp === 'sudah_upload' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    KJP: {school.jumlah_penerima_kjp > 0 ? `${school.jumlah_penerima_kjp} siswa` : school.status_kjp === 'sudah_upload' ? 'Ada' : 'Belum'}
                  </span>
                  <span className={`px-2 py-1 text-xs rounded-full ${school.jumlah_penerima_pip > 0 ? 'bg-teal-100 text-teal-700' : school.status_kip === 'sudah_upload' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    PIP: {school.jumlah_penerima_pip > 0 ? `${school.jumlah_penerima_pip} siswa` : school.status_kip === 'sudah_upload' ? 'Ada' : 'Belum'}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {schools.length > 12 && (
            <div className="text-center mt-6">
              <button
                onClick={() => navigate('/schools')}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Lihat Semua ({schools.length} sekolah)
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Section 4, 5, 6: Default School Details (Flood, KJP, KIP) */}
      {defaultSchool && (
        <section className="py-12 px-4">
          <div className="max-w-7xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <span className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </span>
              Informasi {defaultSchool.nama}
            </h3>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Flood Documentation */}
              <div className="bg-white rounded-xl p-6 shadow-md">
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                  </svg>
                  Status Banjir / Dokumentasi
                </h4>

                <div className="grid grid-cols-3 gap-2 mb-4">
                  {[defaultSchool.foto_gedung, defaultSchool.foto_ruang_kelas, defaultSchool.foto_lapangan].map((photo, idx) => (
                    <div key={idx} className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                      {photo ? (
                        <img src={photo} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <span className={`px-3 py-1 text-sm rounded-full ${defaultSchool.status_dokumentasi === 'selesai_upload' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                  {defaultSchool.status_dokumentasi === 'selesai_upload' ? 'Dokumentasi Lengkap' : 'Menunggu Dokumentasi'}
                </span>
              </div>

              {/* KJP Card */}
              <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                  <span className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-medium rounded-full">
                    KJP
                  </span>
                </div>
                <div className="text-white/80 text-sm mb-1">Penerima Kartu Jakarta Pintar</div>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-white">{defaultSchool.jumlah_penerima_kjp || 0}</span>
                  <span className="text-white/80 text-lg">Siswa</span>
                </div>
              </div>

              {/* PIP Card */}
              <div className="bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <span className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-medium rounded-full">
                    PIP
                  </span>
                </div>
                <div className="text-white/80 text-sm mb-1">Penerima Program Indonesia Pintar</div>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-white">{defaultSchool.jumlah_penerima_pip || 0}</span>
                  <span className="text-white/80 text-lg">Siswa</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Section: Prestasi Minggu Ini */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <span className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
            </span>
            Prestasi Minggu Ini
          </h3>
          <p className="text-gray-500 mb-6">Sekolah dengan dokumentasi dan capaian terbaru minggu ini</p>

          {prestasiMingguIni.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {prestasiMingguIni.map((prestasi) => {
                const urls = prestasi.photo_urls || [];
                const displayUrls = urls.slice(0, 4);
                const overflow = urls.length - 4;
                return (
                <div
                  key={prestasi.id}
                  className="bg-gray-50 rounded-2xl overflow-hidden border border-gray-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                  role="button"
                  tabIndex={0}
                  aria-label={`Lihat detail prestasi ${prestasi.nama_sekolah}`}
                  onClick={() => {
                    setSelectedPrestasi(prestasi);
                    setShowPrestasiModal(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedPrestasi(prestasi);
                      setShowPrestasiModal(true);
                    }
                  }}
                >
                  {/* Photo Gallery — up to 4 previews with overflow count */}
                  <div className="grid gap-0.5 h-36 bg-gray-200"
                    style={{ gridTemplateColumns: `repeat(${Math.max(displayUrls.length, 1)}, 1fr)` }}
                  >
                    {(displayUrls.length > 0 ? displayUrls : [null]).map((foto, idx) => {
                      const isLast = idx === 3 && overflow > 0;
                      return (
                        <div key={idx} className="overflow-hidden bg-gray-100 relative">
                          {foto ? (
                            <img
                              src={foto}
                              alt={`Foto ${idx + 1} ${prestasi.nama_sekolah}`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-300">
                              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                          {isLast && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                              <span className="text-white text-lg font-bold">+{overflow}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Card Body */}
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h4 className="font-bold text-gray-900 text-base leading-tight">{prestasi.nama_sekolah}</h4>
                      <span className="shrink-0 px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded-full font-medium">
                        Minggu Ini
                      </span>
                    </div>
                    <p className="text-sm font-medium text-blue-600 mb-1">{prestasi.judul}</p>
                    {prestasi.deskripsi && (
                      <p className="text-xs text-gray-500 line-clamp-2">{prestasi.deskripsi}</p>
                    )}
                    {prestasi.tanggal_prestasi && (
                      <p className="text-xs text-gray-400 mt-2">
                        {new Date(prestasi.tanggal_prestasi).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </p>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-2xl p-12 text-center">
              <div className="w-16 h-16 bg-yellow-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-gray-700 mb-2">Belum Ada Prestasi Minggu Ini</h4>
              <p className="text-gray-500">Prestasi dan dokumentasi sekolah akan muncul di sini setiap minggu</p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400">
            PKKS - Sistem Penilaian Kinerja Kepala Sekolah
          </p>
          <p className="text-gray-500 text-sm mt-2">
            &copy; 2026 Dinas Pendidikan Provinsi DKI Jakarta
          </p>
        </div>
      </footer>

      {/* Photo Modal */}
      {showPhotoModal && selectedSchool && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowPhotoModal(false)} />
            <div className="relative bg-white rounded-2xl shadow-xl max-w-2xl w-full p-6">
              <button
                onClick={() => setShowPhotoModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <h3 className="text-xl font-bold text-gray-900 mb-4">{selectedSchool.nama}</h3>
              <p className="text-gray-500 mb-4">NPSN: {selectedSchool.npsn} | {selectedSchool.kecamatan}</p>

              {/* Photos Grid */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-700 mb-3">Dokumentasi Banjir</h4>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { url: selectedSchool.foto_gedung, label: 'Foto Gedung' },
                    { url: selectedSchool.foto_ruang_kelas, label: 'Foto Ruang Kelas' },
                    { url: selectedSchool.foto_lapangan, label: 'Foto Lapangan' }
                  ].map((photo, idx) => (
                    <div key={idx}>
                      <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden mb-1">
                        {photo.url ? (
                          <img src={photo.url} alt={photo.label} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                            <svg className="w-8 h-8 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="text-xs">Belum ada</span>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-center text-gray-500">{photo.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Status Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-xs text-gray-500">Status KJP</span>
                  <p className={`font-medium ${selectedSchool.status_kjp === 'sudah_upload' ? 'text-green-600' : 'text-red-600'}`}>
                    {selectedSchool.status_kjp === 'sudah_upload' ? 'Sudah Upload' : 'Belum Upload'}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-xs text-gray-500">Status KIP</span>
                  <p className={`font-medium ${selectedSchool.status_kip === 'sudah_upload' ? 'text-green-600' : 'text-red-600'}`}>
                    {selectedSchool.status_kip === 'sudah_upload' ? 'Sudah Upload' : 'Belum Upload'}
                  </p>
                </div>
              </div>

              {selectedSchool.website && (
                <a
                  href={selectedSchool.website.startsWith('http') ? selectedSchool.website : `https://${selectedSchool.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-center mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Kunjungi Website Sekolah
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Prestasi Detail Modal */}
      {showPrestasiModal && selectedPrestasi && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-60" onClick={() => setShowPrestasiModal(false)} />
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
              {/* Photo Gallery Header — renders all photos from photo_urls */}
              <div className="grid gap-0.5 h-48 bg-gray-200"
                style={{ gridTemplateColumns: `repeat(${Math.min((selectedPrestasi.photo_urls || []).length || 1, 4)}, 1fr)` }}
              >
                {((selectedPrestasi.photo_urls || []).length > 0 ? selectedPrestasi.photo_urls : [null]).map((foto, idx) => (
                  <div key={idx} className="overflow-hidden bg-gray-100 relative">
                    {foto ? (
                      <img
                        src={foto}
                        alt={`Foto dokumentasi ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-gray-300">
                        <svg className="w-8 h-8 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-xs">Foto {idx + 1}</span>
                      </div>
                    )}
                    <div className="absolute bottom-1 left-1 bg-black/50 text-white text-xs px-1.5 py-0.5 rounded">
                      Foto {idx + 1}
                    </div>
                  </div>
                ))}
              </div>

              {/* Close Button */}
              <button
                onClick={() => setShowPrestasiModal(false)}
                className="absolute top-3 right-3 bg-black/50 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-black/70 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded-full font-medium">
                    Prestasi Minggu Ini
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">{selectedPrestasi.nama_sekolah}</h3>
                <p className="text-base font-semibold text-blue-600 mb-3">{selectedPrestasi.judul}</p>
                {selectedPrestasi.deskripsi && (
                  <p className="text-sm text-gray-600 mb-4">{selectedPrestasi.deskripsi}</p>
                )}
                {selectedPrestasi.kecamatan && (
                  <p className="text-sm text-gray-500 mb-2">
                    <span className="font-medium">Kecamatan:</span> {selectedPrestasi.kecamatan}
                  </p>
                )}
                {selectedPrestasi.tanggal_prestasi && (
                  <p className="text-sm text-gray-500">
                    <span className="font-medium">Tanggal:</span>{' '}
                    {new Date(selectedPrestasi.tanggal_prestasi).toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
